import React from 'react';
import { LayoutDashboard, Inbox, LogOut, Settings, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Avatar from '../ui/Avatar';

export function Sidebar({ active, onNavigate, user, company, onLogout, open = false }) {
  const { t } = useTranslation();

  const fullName = user?.fullname || user?.email || '—';
  const initials = (fullName.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2) || '?').toUpperCase();
  const roleLabel = t('nav.roles_label', { defaultValue: 'Utilisateur' });

  return (
    <aside className={`fixed inset-y-0 left-0 z-40 w-60 bg-primary text-white flex flex-col h-screen flex-shrink-0 transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full'} lg:sticky lg:top-0 lg:z-30 lg:translate-x-0`}>
      {/* Logo */}
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-12 h-9 rounded-md bg-white grid place-items-center overflow-hidden p-0.5 flex-shrink-0">
            <img
              src="/mfb-logo.png"
              alt="MFB"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <div className="font-semibold text-[14px] tracking-tight">{t("brand.name")}</div>
            <div className="text-[10.5px] text-sky-200">Fresh Logistics</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto text-[13px]">
        <div className="text-[10px] uppercase tracking-wider text-sky-300/70 px-2 pt-2 pb-1.5 font-semibold">
          {t('nav.section_label')}
        </div>

        {/* Dashboard link */}
        <button
          onClick={() => onNavigate?.('dashboard')}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-left ${
            active === 'dashboard'
              ? 'bg-white/15 text-white'
              : 'text-sky-100/80 hover:bg-white/5 hover:text-white'
          }`}
        >
          <LayoutDashboard size={15} strokeWidth={1.75} className="flex-shrink-0" />
          <span className="flex-1">{t('nav.dashboard')}</span>
        </button>

        {/* Invoice link */}
        <button
          onClick={() => onNavigate?.('invoices')}
          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-left ${
            active === 'invoices'
              ? 'bg-white/15 text-white'
              : 'text-sky-100/80 hover:bg-white/5 hover:text-white'
          }`}
        >
          <Inbox size={15} strokeWidth={1.75} className="flex-shrink-0" />
          <span className="flex-1">{t('nav.invoices')}</span>
        </button>

        {/* Users link (admin role only) */}
        {user?.role === 'ADMIN' && (
          <button
            onClick={() => onNavigate?.('users')}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-left ${
              active === 'users'
                ? 'bg-white/15 text-white'
                : 'text-sky-100/80 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Users size={15} strokeWidth={1.75} className="flex-shrink-0" />
            <span className="flex-1">{t('nav.users')}</span>
          </button>
        )}

        {/* Settings link (admin only) */}
        {company?.type === 'ADMIN' && user?.role === 'ADMIN' && (
          <button
            onClick={() => onNavigate?.('settings')}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium mb-0.5 transition text-left ${
              active === 'settings'
                ? 'bg-white/15 text-white'
                : 'text-sky-100/80 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Settings size={15} strokeWidth={1.75} className="flex-shrink-0" />
            <span className="flex-1">{t('nav.settings')}</span>
          </button>
        )}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/10 px-3 py-3 flex items-center gap-2.5">
        <Avatar initials={initials} size="sm" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-white truncate">{fullName}</p>
          <p className="text-[10px] text-white/50 truncate">{roleLabel}</p>
        </div>
        <button
          onClick={onLogout}
          title={t('nav.logout')}
          className="w-7 h-7 grid place-items-center rounded-md text-white/50 hover:bg-white/10 hover:text-white transition flex-shrink-0"
        >
          <LogOut size={14} strokeWidth={1.75} />
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
